import { z } from 'zod'

import type { StatusTone } from '@ems/ui/patterns/status-badge'

import { FIELD_KINDS, MEDIA_ACCEPTS, type MediaAccept } from '@ems/cms/fields'
import {
  isMediaWidth,
  MEDIA_RATIOS,
  type FocalPoint,
  type MediaCrop,
  type MediaWidth,
} from '@ems/storage/variants'

import { api } from '#admin/lib/api-client.ts'

/**
 * The CMS, as the editor sees it.
 *
 * **The form is not written here.** `GET /v1/cms/admin/collections` returns the field list the
 * API derives from the same Zod object it validates writes with, and `FieldEditor` renders it.
 * That is the whole of "schema-first, no codegen" on this side: a field added to a collection
 * appears in this console on the next deploy of the API, with no change to the console at all —
 * and, more to the point, a field *removed* stops being editable in the same moment it stops
 * being writable, rather than lingering as a form control whose saves are silently rejected.
 *
 * The shapes below are the ones this console actually reads. They are parsed rather than
 * asserted, for the reason `packages/api-client` gives: the console is a static bundle on
 * CloudFront and being a version behind the API is its normal state for minutes after a release.
 */

export const fieldSchema = z.object({
  name: z.string(),
  /**
   * The vocabulary itself, imported rather than retyped.
   *
   * It was an eleven-entry literal, which made this the third place the kinds were written down
   * — and the one that silently disagreed. `fields.ts` calls the vocabulary "closed, and closed
   * on purpose… the only way to add a twelfth is to edit this file and the editor that renders
   * it in the same commit"; a copy here means a twelfth kind parses as invalid in the console
   * while being perfectly valid everywhere else, and the field vanishes from the form.
   */
  kind: z.enum(FIELD_KINDS),
  label: z.string(),
  help: z.string().nullable(),
  placeholder: z.string().nullable().default(null),
  wide: z.boolean(),
  group: z.string().nullable().default(null),
  required: z.boolean(),
  nullable: z.boolean(),
  maxLength: z.number().int().nullable(),
  maxItems: z.number().int().nullable().default(null),
  options: z.array(z.string()).nullable(),
  accept: z.enum(MEDIA_ACCEPTS).nullable().default(null),
  slot: z
    .object({
      ratio: z.enum(MEDIA_RATIOS).nullable(),
      widths: z.array(
        z.custom<MediaWidth>((value) => typeof value === 'number' && isMediaWidth(value)),
      ),
      sizes: z.string(),
    })
    .nullable()
    .default(null),
})

export type Field = z.infer<typeof fieldSchema>

export const collectionSchema = z.object({
  key: z.string(),
  label: z.string(),
  singular: z.string(),
  localized: z.boolean(),
  titleField: z.string(),
  routeKind: z.enum(['collection', 'singleton', 'embedded']),
  routePrefix: z.string().nullable(),
  /** Defaulted, so a console deployed before the API still parses the list. */
  composedInto: z.array(z.string()).default([]),
  seo: z.boolean(),
  fields: z.array(fieldSchema),
})

export type Collection = z.infer<typeof collectionSchema>

export function collectionAnchor(key: string): string {
  return `collection-${key}`
}

const seoSchema = z.object({
  title: z.string().nullable(),
  description: z.string().nullable(),
  imageKey: z.string().nullable(),
  noindex: z.boolean(),
  canonicalPath: z.string().nullable(),
})

export const documentSummarySchema = z.object({
  id: z.string(),
  collection: z.string(),
  slug: z.string(),
  locale: z.string(),
  status: z.enum(['draft', 'scheduled', 'published']),
  title: z.string(),
  version: z.number().int(),
  /** Moves on every write to `data`. A save sends it back. Optional for an older API. */
  revision: z.number().int().optional(),
  /** Published, with saved changes that the site does not show yet. */
  hasUnpublishedChanges: z.boolean().default(false),
  publishedAt: z.string().nullable(),
  scheduledFor: z.string().nullable(),
  updatedAt: z.string(),
  /** Defaulted for the same reason as `composedInto`: the header omits it when it is absent. */
  createdAt: z.string().nullable().default(null),
  path: z.string().nullable(),
  /** `data.sortOrder`: the order of an embedded list on the site. Defaulted for an older API. */
  sortOrder: z.number().nullable().default(null),
  /** `data.tier`: a sponsor's band on the site. Defaulted for an older API. */
  tier: z.string().nullable().default(null),
})

export type DocumentSummary = z.infer<typeof documentSummarySchema>

export const documentSchema = documentSummarySchema.extend({
  data: z.record(z.string(), z.unknown()),
  seo: seoSchema,
  /**
   * The address the site shows it at now: the published copy's. Null when it is not published or
   * its collection has no pages. `.default(null)`: the console is deployed ahead of the API.
   */
  livePath: z.string().nullable().default(null),
})

export type CmsDocument = z.infer<typeof documentSchema>

export const versionSchema = z.object({
  id: z.string(),
  version: z.number().int(),
  slug: z.string(),
  note: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
})

export type Version = z.infer<typeof versionSchema>

const collectionListSchema = z.object({ items: z.array(collectionSchema) })
const documentListSchema = z.object({ items: z.array(documentSummarySchema) })
const versionListSchema = z.object({ items: z.array(versionSchema) })
const publishedSchema = documentSummarySchema.extend({ invalidated: z.array(z.string()) })
const previewSchema = z.object({ token: z.string(), url: z.string(), expiresAt: z.string() })
/**
 * What registering returns: the row, and what the server did to the file on the way in.
 *
 * `objectKey` is the key the library holds, which is not always the key that was uploaded — a
 * JPEG or PNG is stored as WebP under a key of its own. `normalization` is optional because an
 * API older than that change does not send it, and the console is deployed first.
 */
const mediaSchema = z.object({
  id: z.string(),
  objectKey: z.string(),
  normalization: z.object({ converted: z.boolean(), skipped: z.string().nullable() }).optional(),
})

export const mediaSourceSchema = z.object({
  url: z.string(),
  contentType: z.string(),
  altText: z.string().nullable(),
  expiresInSeconds: z.number().int(),
})

export type MediaSource = z.infer<typeof mediaSourceSchema>

export const mediaItemSchema = z.object({
  id: z.string(),
  objectKey: z.string(),
  filename: z.string(),
  contentType: z.string(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  altText: z.string().nullable(),
  focalX: z.number().int(),
  focalY: z.number().int(),
  crop: z
    .object({
      x: z.number().int(),
      y: z.number().int(),
      width: z.number().int(),
      height: z.number().int(),
    })
    .nullable()
    .default(null),
  createdAt: z.string(),
  url: z.string(),
  expiresInSeconds: z.number().int(),
})

export type MediaItem = z.infer<typeof mediaItemSchema>

const mediaPageSchema = z.object({
  items: z.array(mediaItemSchema),
  nextCursor: z.string().nullable(),
})

export function fetchCollections(signal?: AbortSignal): Promise<{ items: Collection[] }> {
  return api.get('/v1/cms/admin/collections', {
    schema: collectionListSchema,
    ...(signal ? { signal } : {}),
  })
}

export function fetchDocuments(
  filter: { collection?: string; status?: string },
  signal?: AbortSignal,
): Promise<{ items: DocumentSummary[] }> {
  const query = new URLSearchParams()
  if (filter.collection !== undefined) query.set('collection', filter.collection)
  if (filter.status !== undefined) query.set('status', filter.status)

  return api.get(`/v1/cms/admin/documents?${query.toString()}`, {
    schema: documentListSchema,
    ...(signal ? { signal } : {}),
  })
}

export function fetchDocument(id: string, signal?: AbortSignal): Promise<CmsDocument> {
  return api.get(`/v1/cms/admin/documents/${id}`, {
    schema: documentSchema,
    ...(signal ? { signal } : {}),
  })
}

export function fetchVersions(id: string, signal?: AbortSignal): Promise<{ items: Version[] }> {
  return api.get(`/v1/cms/admin/documents/${id}/versions`, {
    schema: versionListSchema,
    ...(signal ? { signal } : {}),
  })
}

/**
 * Create a draft.
 *
 * The slug is **omitted**, not computed here: the API derives it from the collection's title
 * field, and a second derivation in the console is a second answer — the one that disagrees
 * being the one that produces a page at an address nobody expected. A singleton ignores it
 * entirely, which is another thing only the API knows.
 */
export function createDocument(input: {
  collection: string
  data: Record<string, unknown>
  locale?: string
}): Promise<CmsDocument> {
  return api.post('/v1/cms/admin/documents', { schema: documentSchema, body: input })
}

export const editionLocalesSchema = z.object({
  defaultLocale: z.string(),
  locales: z.array(z.string()),
})

export type EditionLocales = z.infer<typeof editionLocalesSchema>

export function fetchEditionLocales(signal?: AbortSignal): Promise<EditionLocales> {
  return api.get('/v1/cms/admin/locales', {
    schema: editionLocalesSchema,
    ...(signal ? { signal } : {}),
  })
}

export function createTranslation(id: string, locale: string): Promise<CmsDocument> {
  return api.post(`/v1/cms/admin/documents/${id}/translations`, {
    schema: documentSchema,
    body: { locale },
  })
}

export function saveDocument(
  id: string,
  body: {
    data: Record<string, unknown>
    seo?: unknown
    slug?: string
    expectedVersion: number
    expectedRevision?: number
  },
): Promise<CmsDocument> {
  return api.put(`/v1/cms/admin/documents/${id}`, { schema: documentSchema, body })
}

export function publishDocument(id: string, note?: string) {
  return api.post(`/v1/cms/admin/documents/${id}/publish`, {
    schema: publishedSchema,
    body: note === undefined ? {} : { note },
  })
}

export function unpublishDocument(id: string) {
  return api.post(`/v1/cms/admin/documents/${id}/unpublish`, { schema: publishedSchema, body: {} })
}

/**
 * Put the items of one embedded list in a new order. Every id of the list, first to last, in one
 * collection and one language. The server writes each position into `sortOrder`.
 */
export function reorderDocuments(ids: string[]): Promise<{ invalidated: string[] }> {
  return api.post('/v1/cms/admin/documents/reorder', {
    schema: z.object({ invalidated: z.array(z.string()) }),
    body: { ids },
  })
}

export function deleteDocument(id: string): Promise<{ deleted: boolean }> {
  return api.delete(`/v1/cms/admin/documents/${id}`, {
    schema: z.object({ deleted: z.boolean() }),
  })
}

export function scheduleDocument(id: string, scheduledFor: string) {
  return api.post(`/v1/cms/admin/documents/${id}/schedule`, {
    schema: documentSummarySchema,
    body: { scheduledFor },
  })
}

export function rollbackDocument(id: string, version: number, expectedRevision?: number) {
  return api.post(`/v1/cms/admin/documents/${id}/rollback`, {
    schema: publishedSchema,
    body: expectedRevision === undefined ? { version } : { version, expectedRevision },
  })
}

const siteSchema = z.object({ origin: z.string().nullable() })

/**
 * The address of the edition's public site, for example `https://iigce.example`. Null when the
 * edition has no verified domain. The live page and a media file's link start from it.
 */
export function fetchSite(signal?: AbortSignal) {
  return api.get('/v1/cms/admin/site', { schema: siteSchema, ...(signal ? { signal } : {}) })
}

/**
 * Where the site serves a media file: `/media/<key>`, each segment encoded. The same address the
 * website builds (`mediaUrl` in `apps/website`).
 */
export function mediaPath(objectKey: string): string {
  return `/media/${objectKey.split('/').map(encodeURIComponent).join('/')}`
}

/** The full public URL of a path on the site. Null when the site has no address. */
export function siteUrl(origin: string | null, path: string): string | null {
  return origin === null ? null : `${origin}${path.startsWith('/') ? path : `/${path}`}`
}

export function requestPreview(id: string, versionId: string | null = null) {
  return api.post(`/v1/cms/admin/documents/${id}/preview`, {
    schema: previewSchema,
    body: { versionId },
  })
}

/**
 * Record an object that is already in storage, so the site will serve it.
 *
 * The bytes never pass through here: `uploadFile` has already put them in the bucket, and this
 * only tells the library the key exists. Registering the same key twice returns the same row.
 *
 * The server reads the file itself and records its own type, size and dimensions; the ones sent
 * here are what an older API still requires, and a current one ignores them.
 */
export function registerMedia(input: {
  objectKey: string
  filename: string
  contentType: string
  byteSize: number
  width?: number
  height?: number
}) {
  return api.post('/v1/cms/admin/media', { schema: mediaSchema, body: input })
}

/**
 * One page of the library's images, newest first, each with a thumbnail URL already signed.
 *
 * `kind=image` is asked for rather than filtered here: the server pages after filtering, so a
 * client-side filter would hand the picker short pages and a cursor that skips nothing it can see.
 */
export function fetchMediaLibrary(
  filter: { q: string; accept: MediaAccept; cursor: string | undefined },
  signal?: AbortSignal,
): Promise<z.infer<typeof mediaPageSchema>> {
  const query = new URLSearchParams({ limit: '40' })
  if (filter.accept === 'image') query.set('kind', 'image')
  if (filter.q !== '') query.set('q', filter.q)
  if (filter.cursor !== undefined) query.set('cursor', filter.cursor)

  return api.get(`/v1/cms/admin/media?${query.toString()}`, {
    schema: mediaPageSchema,
    ...(signal ? { signal } : {}),
  })
}

/**
 * The library row behind a key a field already holds — its id, its focal point and a URL.
 *
 * `null` for a key the library does not know, which an imported document can carry.
 */
export async function fetchMediaByKey(
  objectKey: string,
  signal?: AbortSignal,
): Promise<MediaItem | null> {
  const query = new URLSearchParams({ key: objectKey, limit: '1' })
  const page = await api.get(`/v1/cms/admin/media?${query.toString()}`, {
    schema: mediaPageSchema,
    ...(signal ? { signal } : {}),
  })
  return page.items[0] ?? null
}

export function saveMediaEdit(id: string, edit: FocalPoint & { crop: MediaCrop | null }) {
  return api.patch(`/v1/cms/admin/media/${id}`, {
    schema: z.object({ id: z.string() }),
    body: edit,
  })
}

/**
 * A short-lived URL for one registered key, for a preview.
 *
 * Only registered media resolves; any other key is a 404, which is also what a key typed by hand
 * before the library existed will get.
 */
export function fetchMediaSource(objectKey: string, signal?: AbortSignal): Promise<MediaSource> {
  return api.get(`/v1/cms/content/media?key=${encodeURIComponent(objectKey)}`, {
    schema: mediaSourceSchema,
    ...(signal ? { signal } : {}),
  })
}

/**
 * A blank document for a collection, built from its field list.
 *
 * Every field gets a value of the right *kind* rather than `undefined`, so the form's inputs are
 * controlled from the first render. An input that starts `undefined` and becomes a string on the
 * first keystroke is React's "changing an uncontrolled input to be controlled" warning, and the
 * symptom users see is a cursor that jumps to the end of the field.
 */
export function blankDocument(collection: Collection): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const field of collection.fields) {
    const value = emptyValue(field)
    // `undefined` means "this kind has no valid empty value" — a date, a URL and an email are
    // all refused as `''`. The key is omitted rather than sent, which the draft's partial
    // validation accepts and the editor then fills in.
    if (value !== undefined) out[field.name] = value
  }
  return out
}

/**
 * The field a collection is named by — its label and length limit are what "New" asks for.
 *
 * `titleField` is checked to be a `line` when the collection is defined, so it is always in the
 * list; `undefined` here means a descriptor the API should never have served.
 */
export function titleFieldOf(collection: Collection): Field | undefined {
  return collection.fields.find((field) => field.name === collection.titleField)
}

/**
 * What "New" posts: a blank document, with the title already in it.
 *
 * A singleton takes no title — its slug is fixed, so it is created empty. Every other
 * collection's slug is derived from the title **at creation**, and the API refuses a title that
 * yields none; the editor has no slug input, so this is also the one moment the address is chosen.
 */
export function newDocumentData(collection: Collection, title?: string): Record<string, unknown> {
  const data = blankDocument(collection)
  if (title !== undefined) data[collection.titleField] = title.trim()
  return data
}

export function emptyValue(field: Field): unknown {
  if (field.nullable) return null
  switch (field.kind) {
    case 'boolean':
      return false
    case 'number':
      return 0
    case 'list':
    case 'medialist':
    case 'urllist':
      return []
    case 'select':
      return field.options?.[0] ?? ''
    /**
     * No empty value exists for these.
     *
     * `''` is not a date, not a URL, not an email and not a media key (`min(1)`) — each would
     * be refused even by the draft's partial parse, which checks the type of whatever is
     * present. Omitting the key is the honest representation of "not filled in yet".
     */
    case 'media':
    case 'date':
    case 'url':
    case 'email':
      return undefined
    default:
      return ''
  }
}

/**
 * What an editor should be told about a document's state, in one sentence.
 *
 * Assembled here rather than in the component because three screens show it — the list, the
 * editor's header and the publish confirmation — and three phrasings of the same fact is how a
 * console starts to feel like three consoles.
 */
export function statusLine(document: DocumentSummary): string {
  switch (document.status) {
    case 'published':
      return document.publishedAt === null
        ? 'Published'
        : `Published ${new Date(document.publishedAt).toLocaleString()}`
    case 'scheduled':
      return document.scheduledFor === null
        ? 'Scheduled'
        : `Goes live ${new Date(document.scheduledFor).toLocaleString()}`
    case 'draft':
      return `Draft · last saved ${new Date(document.updatedAt).toLocaleString()}`
    default: {
      // Unreachable while the API and this console agree on the vocabulary, and a throw rather
      // than a fallback string so a fourth status shows up as an error somebody investigates
      // instead of as a document that silently reads "Draft".
      const unreachable: never = document.status
      throw new Error(`Unhandled document status: ${String(unreachable)}`)
    }
  }
}

/**
 * A document's publication state, as a tone.
 *
 * **This replaces the only hardcoded colors left in the product** — `bg-green-100 text-green-900`
 * and `bg-amber-100 text-amber-900` in the content list. Those were light-mode Tailwind palette
 * values on a themed, dark-mode-capable surface, so in dark mode they rendered as two pale blocks
 * on a near-black page.
 *
 * `scheduled` is a warning rather than a success: it is the state with a deadline attached, and the
 * one an editor most often needs to notice before it fires.
 */
const DOCUMENT_TONE: Record<DocumentSummary['status'], StatusTone> = {
  published: 'success',
  scheduled: 'warning',
  draft: 'neutral',
}

export function documentTone(status: DocumentSummary['status']): StatusTone {
  return DOCUMENT_TONE[status]
}

export type FieldGroup = {
  key: string
  title: string | null
  fields: Field[]
}

/**
 * The fields that render as form controls: all but a `sections` field, which the editor renders
 * as the page's layout.
 */
export function formFields(fields: readonly Field[]): Field[] {
  return fields.filter((field) => field.kind !== 'sections')
}

export function groupFields(fields: readonly Field[]): FieldGroup[] {
  const groups = new Map<string, FieldGroup>()
  for (const field of fields) {
    const key = field.group ?? ''
    const existing = groups.get(key)
    if (existing === undefined) {
      groups.set(key, {
        key: key === '' ? '__ungrouped__' : key,
        title: field.group,
        fields: [field],
      })
    } else {
      existing.fields.push(field)
    }
  }
  return [...groups.values()]
}
